import { useState, useRef, useEffect, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

interface PullToRefreshProps {
  onRefresh: () => Promise<void>
  children: ReactNode
  threshold?: number
}

export default function PullToRefresh({ onRefresh, children, threshold = 70 }: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isPulling, setIsPulling] = useState(false)
  const startY = useRef(0)
  const currentY = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const touchStartTime = useRef(0)

  // Force reset function to prevent stuck states
  const forceReset = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    setIsPulling(false)
    setPullDistance(0)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isRefreshing) return

    const container = containerRef.current
    if (!container) return

    // Check if we're at the top (with small threshold for reliability)
    const isAtTop = container.scrollTop <= 5

    if (isAtTop) {
      touchStartTime.current = Date.now()
      startY.current = e.touches[0].clientY
      currentY.current = e.touches[0].clientY
      setIsPulling(true)
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isPulling || isRefreshing) return

    const container = containerRef.current
    if (!container) return

    // Stop pulling if user scrolled down
    if (container.scrollTop > 5) {
      forceReset()
      return
    }

    currentY.current = e.touches[0].clientY
    const distance = currentY.current - startY.current

    // Only track downward pulls
    if (distance > 0) {
      // Apply stronger resistance for smoother feel
      const resistance = 0.35
      const adjustedDistance = Math.min(distance * resistance, threshold * 1.8)

      // Use RAF for smoother animation
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }

      rafRef.current = requestAnimationFrame(() => {
        setPullDistance(adjustedDistance)
      })

      // Prevent default scroll behavior when pulling significantly
      if (adjustedDistance > 5) {
        e.preventDefault()
      }
    } else {
      // Reset if pulling upward
      forceReset()
    }
  }

  const handleTouchEnd = async () => {
    if (!isPulling) return

    setIsPulling(false)

    // Cancel any pending animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    // Check if pull was long enough and fast enough
    const touchDuration = Date.now() - touchStartTime.current
    const shouldRefresh = pullDistance >= threshold && touchDuration < 2000

    if (shouldRefresh && !isRefreshing) {
      setIsRefreshing(true)
      setPullDistance(threshold) // Keep at threshold during refresh

      try {
        await onRefresh()
        // Add small delay for better UX
        await new Promise(resolve => setTimeout(resolve, 300))
      } catch (error) {
        console.error('Refresh failed:', error)
      } finally {
        setIsRefreshing(false)
        // Smooth reset after a short delay
        setTimeout(() => setPullDistance(0), 100)
      }
    } else {
      // Quick snap back
      setPullDistance(0)
    }
  }

  // Cancel on touch cancel (e.g., system gesture)
  const handleTouchCancel = () => {
    forceReset()
  }

  // Auto-reset if stuck
  useEffect(() => {
    if (isPulling && !isRefreshing) {
      const timeout = setTimeout(() => {
        console.warn('Pull-to-refresh stuck, force resetting')
        forceReset()
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [isPulling, isRefreshing])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  const progress = Math.min(pullDistance / threshold, 1)
  const opacity = Math.min(progress * 1.2, 1)
  const scale = Math.min(0.4 + progress * 0.6, 1)

  return (
    <div
      ref={containerRef}
      className="relative h-full overflow-y-auto"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      style={{
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Pull-to-refresh indicator */}
      <div
        className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-10"
        style={{
          top: `${Math.max(0, Math.min(pullDistance * 0.65, threshold * 0.65))}px`,
          opacity: opacity,
          transition: isPulling ? 'none' : 'opacity 0.2s ease-out, top 0.3s ease-out',
        }}
      >
        <div
          className="flex items-center justify-center bg-tg-button/10 rounded-full p-2"
          style={{
            transform: `scale(${scale})`,
            transition: isPulling ? 'none' : 'transform 0.2s ease-out',
          }}
        >
          <Loader2
            className="w-6 h-6 text-tg-button"
            style={{
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
              transform: isRefreshing ? 'none' : `rotate(${progress * 360}deg)`,
              transition: isRefreshing ? 'none' : 'transform 0.1s ease-out',
            }}
          />
        </div>
      </div>

      {/* Content with dynamic transform */}
      <div
        style={{
          transform: `translateY(${pullDistance}px)`,
          transition: isPulling ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {children}
      </div>
    </div>
  )
}
